import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle, XCircle, X } from "lucide-react";

const ToastContext = createContext(null);

const style = document.createElement("style");
style.textContent = `
  @keyframes toast-in {
    from { transform: translateY(10px) scale(0.96); opacity: 0; }
    to   { transform: translateY(0)    scale(1);    opacity: 1; }
  }
`;
document.head.appendChild(style);

let uid = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) =>
    setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  const push = useCallback((type, message) => {
    const id = ++uid;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => dismiss(id), 3500);
  }, [dismiss]);

  const toast = useMemo(() => ({
    success: (msg) => push("success", msg),
    error:   (msg) => push("error",   msg),
  }), [push]);

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <div className="fixed bottom-5 right-5 z-[9999] flex flex-col-reverse gap-2 items-end pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            style={{ animation: "toast-in 0.22s ease" }}
            className={`flex items-start gap-3 px-4 py-3.5 rounded-2xl shadow-lg max-w-[340px] min-w-[240px] pointer-events-auto bg-white ${
              t.type === "success" ? "border border-green-100" : "border border-red-100"
            }`}
          >
            {t.type === "success" ? (
              <CheckCircle size={17} className="text-green-500 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
            ) : (
              <XCircle size={17} className="text-red-500 flex-shrink-0 mt-0.5" strokeWidth={2.2} />
            )}
            <p className="flex-1 text-[13px] font-manrope-regular text-gray-700 leading-snug">{t.message}</p>
            <button
              onClick={() => dismiss(t.id)}
              className="text-gray-400 hover:text-gray-600 transition-all flex-shrink-0 mt-0.5"
            >
              <X size={13} strokeWidth={2} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
